import React, { type JSX } from "react";

import { H3 } from "@impero-com/common/components/html";
import * as DV from "@impero-com/common/utils/dataView";
import { t } from "@impero-com/common/utils/i18n";
import { Divider } from "@impero-com/ui";
import { default as Form } from "antd/es/form";

import {
    ControlOverviewListDto,
    RiskDirectoryNodeWithDetailsDto as RiskDirectoryNodeDto,
    MetadataInstanceValue,
    UpdatedMitigatingControl,
    EntityView,
} from "api/generated/model";
import { TextAreaInput } from "components/form/legacyFields/textAreaInput";
import * as R from "org/risks/types";
import { UpdateRootNodeLinkedResourcesCallback } from "org/risks/widgets/attachedFilesField";
import { MitigatingControls } from "org/risks/widgets/mitigatingControls";
import { NodeScope } from "org/risks/widgets/nodeScope";
import { Guid } from "utils/guid";

import {
    CustomizedValidations,
    MetadataFieldSectionsLayout,
    PartialSharedProperties,
} from "../../sections";
import * as T from "../../types";
import { forwardToRisk, getMitigatingControls, getScope } from "../../utils";

import * as theme from "./theme.css";

class MapDrawerForm extends React.Component<MapDrawerFormProps> {
    render() {
        const {
            node,
            entities,
            riskDirectoryFieldCount,
            riskDirectoryLevel,
            metadataExtractors,
            entityFieldCount,
            entityLevel,
            mitigatingControlsProps,
            common,
            allControls,
            rootNode,
            assessmentStatus,
            specialProperties,
            getResponse,
            updateRootNodeLinkedResources,
        } = this.props;

        const entityLevelProps = {
            ...this.props.entityLevelProps,
        };

        let entityFieldEditProperties = {};

        if (entityLevelProps.type === "EDIT") {
            entityFieldEditProperties = {
                customizedValidations: this.props.customizedValidations,
            };
        }

        return (
            <Form>
                <H3 className={theme.nameNode}>{getName(node)}</H3>
                <TextAreaInput
                    type="READ_ONLY"
                    label={t("RiskDirectory_NodeUpdate_Field_Description")}
                    value={getDescription(node)}
                />
                <br />
                <NodeScope
                    type="READ_ONLY"
                    entities={entities}
                    nodeType={
                        node.type === "ExpandedRiskNode" ? "Risk" : "Process"
                    }
                    {...getScope(node)}
                />
                {riskDirectoryFieldCount > 0 ? (
                    <>
                        <Divider />
                        <MetadataFieldSectionsLayout
                            type="READ_ONLY"
                            sections={riskDirectoryLevel}
                            extractors={metadataExtractors}
                            node={node}
                            updateRootNodeLinkedResources={
                                updateRootNodeLinkedResources
                            }
                        />
                    </>
                ) : null}
                {entityFieldCount > 0 ? (
                    <>
                        <Divider />
                        <MetadataFieldSectionsLayout
                            {...entityLevelProps}
                            sections={entityLevel}
                            extractors={metadataExtractors}
                            specialProperties={specialProperties}
                            updateRootNodeLinkedResources={
                                updateRootNodeLinkedResources
                            }
                            {...entityFieldEditProperties}
                        />
                    </>
                ) : null}
                {getResponse() === R.Response.Reduce ? (
                    <>
                        <br />
                        <MitigatingControls
                            {...mitigatingControlsProps}
                            mitigatingControls={getMitigatingControls(node)}
                            controlIdToControlAndActions={common.controlIdToControlAndActions(
                                rootNode,
                                allControls
                            )}
                            allControls={allControls}
                            error={
                                this.props.customizedValidations?.[
                                    "mitigatingControls"
                                ]?.help
                            }
                        />
                    </>
                ) : null}

                {assessmentStatus}
            </Form>
        );
    }
}

const getName = forwardToRisk((node) => node.content.name);
const getDescription = forwardToRisk((node) => node.content.description);

interface MapDrawerFormProps {
    node: R.ProcessNode | T.ExpandedRiskNode;
    rootNode: RiskDirectoryNodeDto;
    entities: Record<number, EntityView>;
    common: T.CommonGetters;
    allControls: ControlOverviewListDto;
    riskDirectoryFieldCount: number;
    riskDirectoryLevel: {
        id: string;
        sectionColumns: DV.DataExtractor<T.NodeWithMD, T.MetadataInstance>[][];
    }[];
    metadataExtractors: Array<
        DV.DataExtractor<T.NodeWithMD, T.MetadataInstance>
    >;
    entityFieldCount: number;
    entityLevel: {
        id: string;
        sectionColumns: DV.DataExtractor<T.NodeWithMD, T.MetadataInstance>[][];
    }[];
    entityLevelProps:
        | { type: "READ_ONLY"; node: R.ProcessNode | T.ExpandedRiskNode }
        | {
              type: "EDIT";
              node: R.ProcessNode | T.ExpandedRiskNode;
              onChangeFieldValueAsync: <PV>(
                  fieldId: Guid,
                  promiseValue: Promise<PV>,
                  callback: (
                      prevState: MetadataInstanceValue | undefined,
                      promisedValue: PV
                  ) => MetadataInstanceValue | null
              ) => Promise<void>;
          };
    mitigatingControlsProps:
        | { type: "READ_ONLY" }
        | {
              type: "EDIT";
              onUpdate: (
                  callback: (
                      mitigatingControls:
                          | Array<UpdatedMitigatingControl>
                          | undefined
                  ) => Array<UpdatedMitigatingControl> | undefined
              ) => Promise<void>;
          };

    assessmentStatus: JSX.Element | null;

    customizedValidations?: CustomizedValidations;

    specialProperties(
        extractor: DV.DataExtractor<T.NodeWithMD, T.MetadataInstance>,
        instance: T.MetadataInstance,
        _type: "READ_ONLY" | "EDIT" | "CREATE"
    ): PartialSharedProperties | undefined;

    getResponse(): R.ResponseEnum | undefined;
    updateRootNodeLinkedResources: UpdateRootNodeLinkedResourcesCallback;
}

export type { MapDrawerFormProps };
export default MapDrawerForm;
